import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  DoorFront as DoorFrontIcon,
  MeetingRoom as MeetingRoomIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { getAttendanceSummaryCards } from '../utils/api';

const EMPTY_METRICS = {
  totalIn: 0,
  totalOut: 0,
  presenceLabel: '0m',
  inPremisesLabel: '0m',
  outPremisesLabel: '0m',
  premisesStatus: '-', // 'in' | 'out' | '-'
};

const getTimeInDisplayTimezone = (dateValue) => {
  let d = null;
  if (dateValue instanceof Date) {
    d = dateValue;
  } else if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    const utcString = trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`;
    d = new Date(utcString);
  } else {
    d = new Date(dateValue);
  }

  if (!d || Number.isNaN(d.getTime())) return { hours: 0, minutes: 0, seconds: 0 };

  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const hours = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  const minutes = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
  const seconds = parseInt(parts.find((p) => p.type === 'second')?.value || '0', 10);
  return { hours, minutes, seconds };
};

const formatMinutesLabel = (totalMins) => {
  const safe = Math.max(0, Math.floor(totalMins || 0));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

/** Today window: 8:00–now. Returns in/out minutes and latest action. */
const calculateTodayInOutMinutes = (events) => {
  const startHour = 8;
  const endHour = 24;
  const startRange = startHour * 60;
  const endRange = endHour * 60;
  const nowTz = getTimeInDisplayTimezone(new Date());
  const endMinute = Math.max(startRange, Math.min(endRange, nowTz.hours * 60 + nowTz.minutes));

  let inMins = 0;
  let outMins = 0;
  let lastCheckInMinute = null;
  let lastCheckOutMinute = null;
  let latestAction = null;

  const sortedEvents = [...(events || [])].sort(
    (a, b) => new Date(a.created_at || a.timestamp) - new Date(b.created_at || b.timestamp)
  );

  sortedEvents.forEach((event) => {
    const action = event.action === 'check_in' || event.action === 'check_out' ? event.action : null;
    if (!action) return;
    latestAction = action;
    const { hours, minutes } = getTimeInDisplayTimezone(event.created_at || event.timestamp);
    const eventMinute = Math.max(startRange, Math.min(endRange, hours * 60 + minutes));

    if (action === 'check_in') {
      if (lastCheckOutMinute !== null) {
        if (eventMinute > lastCheckOutMinute) {
          outMins += eventMinute - lastCheckOutMinute;
        }
        lastCheckOutMinute = null;
      }
      if (lastCheckInMinute !== null && eventMinute > lastCheckInMinute) {
        inMins += eventMinute - lastCheckInMinute;
      }
      lastCheckInMinute = eventMinute;
    } else if (action === 'check_out') {
      // Consecutive Out → Out: count the gap as out-premises time
      if (lastCheckOutMinute !== null && lastCheckInMinute === null) {
        if (eventMinute > lastCheckOutMinute) {
          outMins += eventMinute - lastCheckOutMinute;
        }
      }
      if (lastCheckInMinute !== null) {
        if (eventMinute > lastCheckInMinute) {
          inMins += eventMinute - lastCheckInMinute;
        }
        lastCheckInMinute = null;
      }
      lastCheckOutMinute = eventMinute;
    }
  });


  // Do NOT count Out → now after a final checkout.
  if (lastCheckInMinute !== null && endMinute > lastCheckInMinute) {
    inMins += endMinute - lastCheckInMinute;
  }

  return { inMins, outMins, latestAction };
};

const buildTodayDateTimeRangeUtc = () => {
  const today = new Date();
  const startDateIST = new Date(Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    0,
    0,
    0
  ));
  const endDateIST = new Date(Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    0
  ));
  const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);
  const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);
  return {
    formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
    formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
  };
};

/**
 * Today-only attendance metric cards for Employee / Vendor / Visitor detail pages.
 * Owns API fetch, duration/status calculation, and card UI.
 */
const TodayAttendanceMetricsCards = ({
  personId,
  enabled = true,
  isVendor = false,
  isVisitor = false,
}) => {
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [loading, setLoading] = useState(false);

  const fetchTodayMetrics = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { formattedStart, formattedEnd } = buildTodayDateTimeRangeUtc();
      const res = await getAttendanceSummaryCards({
        person_id: id,
        start_time: formattedStart,
        end_time: formattedEnd,
        days: null,
        limit: 1,
        offset: 0,
        is_intrusion: false,
        is_vendor: isVendor ? true : null,
        is_visitor: isVisitor ? true : null,
      });
      const summary = Array.isArray(res?.summaries) ? res.summaries[0] : null;
      const events = summary?.recent_events || [];
      const { inMins, outMins, latestAction } = calculateTodayInOutMinutes(events);
      const premisesStatus =
        latestAction === 'check_in' ? 'in' : latestAction === 'check_out' ? 'out' : '-';
      setMetrics({
        totalIn: Number(summary?.total_check_ins || 0),
        totalOut: Number(summary?.total_check_outs || 0),
        presenceLabel: formatMinutesLabel(inMins),
        inPremisesLabel: formatMinutesLabel(inMins),
        outPremisesLabel: formatMinutesLabel(outMins),
        premisesStatus,
      });
    } catch (err) {
      console.error('Failed to fetch today metrics:', err);
      setMetrics(EMPTY_METRICS);
    } finally {
      setLoading(false);
    }
  }, [isVendor, isVisitor]);

  useEffect(() => {
    if (personId && enabled) {
      fetchTodayMetrics(personId);
    }
  }, [personId, enabled, fetchTodayMetrics]);

  const cards = [
    {
      key: 'status',
      label: 'Current Status',
      value:
        metrics.premisesStatus === 'in'
          ? 'In Premises'
          : metrics.premisesStatus === 'out'
            ? 'Out Premises'
            : '-',
      icon: metrics.premisesStatus === 'in' ? DoorFrontIcon : MeetingRoomIcon,
      color:
        metrics.premisesStatus === 'in'
          ? '#166534'
          : metrics.premisesStatus === 'out'
            ? '#b91c1c'
            : '#64748b',
      bg:
        metrics.premisesStatus === 'in'
          ? '#dcfce7'
          : metrics.premisesStatus === 'out'
            ? '#fee2e2'
            : '#f1f5f9',
    },
    {
      key: 'total-in',
      label: 'Total In',
      value: metrics.totalIn,
      icon: LoginIcon,
      color: '#166534',
      bg: '#dcfce7',
    },
    {
      key: 'total-out',
      label: 'Total Out',
      value: metrics.totalOut,
      icon: LogoutIcon,
      color: '#854d0e',
      bg: '#fef9c3',
    },
    {
      key: 'in-premises-time',
      label: 'In Premises Time',
      value: metrics.inPremisesLabel,
      icon: DoorFrontIcon,
      color: '#c2410c',
      bg: '#ffedd5',
    },
    {
      key: 'out-premises-time',
      label: 'Out Premises Time',
      value: metrics.outPremisesLabel,
      icon: MeetingRoomIcon,
      color: '#6d28d9',
      bg: '#ede9fe',
    },
    {
      key: 'presence',
      label: 'Total Presence',
      value: metrics.presenceLabel,
      icon: TimerIcon,
      color: '#1d4ed8',
      bg: '#dbeafe',
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, minmax(0, 1fr))',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
        },
        gap: 1,
        alignContent: 'start',
      }}
    >
      {loading ? (
        Array.from({ length: 6 }).map((_, idx) => (
          <Paper
            key={`metric-skel-${idx}`}
            variant="outlined"
            sx={{ p: 1, borderRadius: 1.5, borderColor: 'divider' }}
          >
            <Skeleton width="55%" height={12} />
            <Skeleton width="40%" height={22} sx={{ mt: 0.5 }} />
          </Paper>
        ))
      ) : (
        cards.map((card) => {
          const Icon = card.icon;
          return (
            <Tooltip key={card.key} title={card.label} arrow>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1, md: 1.25, lg: 1 },
                  borderRadius: 1.5,
                  borderColor: 'divider',
                  bgcolor: card.bg,
                  display: 'flex',
                  flexDirection: { xs: 'row', md: 'column', lg: 'row' },
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', md: 'center', lg: 'flex-start' },
                  gap: { xs: 1, md: 0.75, lg: 1 },
                  minHeight: { xs: 62, md: 84, lg: 62 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 28, md: 32, lg: 28 },
                    height: { xs: 28, md: 32, lg: 28 },
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.7)',
                    flexShrink: 0,
                    color: card.color,
                  }}
                >
                  <Icon sx={{ fontSize: { xs: 16, md: 18, lg: 16 } }} />
                </Box>
                <Box
                  sx={{
                    minWidth: 0,
                    width: { xs: 'auto', md: '100%', lg: 'auto' },
                    textAlign: { xs: 'left', md: 'center', lg: 'left' },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: 'block', md: 'none', lg: 'block' },
                      lineHeight: 1.2,
                      color: 'text.secondary',
                      fontSize: '0.65rem',
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.25,
                      fontSize: { xs: '0.85rem', md: '0.8rem', lg: '0.85rem' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {card.value}
                  </Typography>
                </Box>
              </Paper>
            </Tooltip>
          );
        })
      )}
    </Box>
  );
};

export default TodayAttendanceMetricsCards;
